import {defineStore} from 'pinia'
import {LocalStorage} from "ttl-localstorage";
import AuthService from "@/api/authService";
import {ROLES} from "@/api/models";
import {isNull, itExist} from "@/utils/general-utils";

interface User {
  aud: string;
  iss: string;
  iat: number;
  exp: number; // in UTC-0
  authTime: number;
  sub: string;
  upn: string;
  uniqueName: string;
  pwdUrl: string;
  pwdExp: string;
  sid: string;
  Groups: string[] | string;
  c: string;
  co: string;
  email: string;
  given_name: string;
  winaccountname: string;
}

const STORAGE_KEY = 'auth-store-persist';

export const useAuthStore = defineStore('auth', {
  persist: {
    key: STORAGE_KEY,
    beforeRestore: (context) => {
      if (AuthService.isExpired()) {
        LocalStorage.removeKeySync(STORAGE_KEY);
      }
    },
  },
  state: () => ({
    user: null as null | User,
    roles: [] as ROLES[],
  }),
  getters: {
    isConnected(): boolean {
      return !isNull(this.user);
    },
    isFA(): boolean {
      return this.roles.includes(ROLES.FA);
    },
    isSuperAdmin(): boolean {
      return this.roles.includes(ROLES.SUPER_ADMIN);
    },
    isLocalAdmin(): boolean {
      return this.roles.includes(ROLES.LOCAL_ADMIN);
    },
    isFAOnly(): boolean {
      return this.isFA && !this.isSuperAdmin && !this.isLocalAdmin;
    },
    isAnyAdmin(): boolean {
      return this.isLocalAdmin || this.isSuperAdmin;
    },
    defaultPageName(): string {
      return this.isAnyAdmin ? 'list' : 'qr-code';
    },
  },
  actions: {
    setUser(user: any): void {
      this.user = {
        aud: user.aud,
        iss: user.iss,
        iat: user.iat,
        exp: user.exp,
        authTime: user.auth_time,
        sub: user.sub,
        upn: user.upn,
        uniqueName: user.unique_name,
        pwdUrl: user.pwd_url,
        pwdExp: user.pwd_exp,
        sid: user.sid,
        Groups: user.Groups,
        c: user.c,
        co: user.co,
        email: user.email,
        given_name: user.given_name,
        winaccountname: user.winaccountname,
      };
    },
    saveData(userInfos: User | null, expiresDate: string): void {
      AuthService.saveExpire(expiresDate);
      if (!itExist(userInfos)) {
        return;
      }
      this.setUser(userInfos);
    },
    setRoles(roles: ROLES[]): void {
      this.roles = roles;
    },
    reset(): void {
      this.user = null;
      this.roles = [];
    },
  },
});
